import { createContext, useEffect, useState, type ReactNode } from 'react';
import { get } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import type { OrgConfig } from '@/types/org';

interface OrgConfigContextType {
  config: OrgConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const OrgConfigContext = createContext<OrgConfigContextType | null>(null);

export function OrgConfigProvider({ children }: { children: ReactNode }) {
  const { user, token } = useAuth();
  const [config, setConfig] = useState<OrgConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = () => {
    if (!token || !user) return;
    setLoading(true);
    setError(null);
    get<OrgConfig>('/org/config')
      .then(setConfig)
      .catch(() => setError('Failed to load organization configuration'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchConfig();
  }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OrgConfigContext.Provider value={{ config, loading, error, refetch: fetchConfig }}>
      {children}
    </OrgConfigContext.Provider>
  );
}
